import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import store from '@lib/store';
import { IAppError } from '@types';
import { SubtleErrorNotification } from './SubtleErrorNotification';

export const SubtleErrorHandler = observer(() => {
  const [displayedError, setDisplayedError] = useState<IAppError | null>(null);

  // Read in the RENDER body, not only inside the effect.
  //
  // MobX's observer only tracks observables touched while rendering. This
  // component read store.errors exclusively inside useEffect — which runs
  // untracked — so nothing ever re-rendered it when an error was pushed. The
  // effect then only re-ran when displayedError changed, i.e. when it had
  // already shown something. The practical result: the queue was a black hole.
  // Every silent failure in the app landed in it and was never displayed,
  // which is why so many failures looked like nothing happening at all.
  const pending = store.errors.length;

  useEffect(() => {
    if (pending > 0 && !displayedError) {
      const error = store.errors.shift();

      if (error && !error.shown) {
        error.shown = true;
        setDisplayedError(error);
      }
    }
  }, [pending, displayedError]);

  // Auto-dismiss, tied to the error actually on screen. The timer used to be
  // created inside the effect above and never cleared, so dismissing one
  // notification early left its timer running to cut the NEXT one short.
  useEffect(() => {
    if (!displayedError) return undefined;

    const timer = setTimeout(() => setDisplayedError(null), 10000);

    return () => clearTimeout(timer);
  }, [displayedError]);

  const handleDismiss = () => {
    setDisplayedError(null);
  };

  return (
    <SubtleErrorNotification 
      error={displayedError} 
      onDismiss={handleDismiss} 
    />
  );
});

export default SubtleErrorHandler;